import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { manageAudition, sortApplication, updateAudition } from '../../redux/audition/actions';
import { useSelector } from 'react-redux';
import { managedAuditionWithSubmissionsSelector } from '../../selectors/selectors';
import { MetaTags } from '../../components/Metatags/Metatags';
import { useDispatchPromise } from '../../utils/AppUtils';
import { usePagination } from '../../hook/usePagination';
import { ManageAuditionContainer } from '../../components/ScreenContainer/ManageAuditionContainer';
import { IApplication } from '../../types/global/ListRender.types';
import { ICreateAuditionPayload } from '../../redux/audition/types';
import { useDispatch } from 'react-redux';

type UpdateField =
    | { key: keyof ICreateAuditionPayload; value: any }
    | { key: keyof ICreateAuditionPayload; value: any }[];

const ManageAudition = () => {
    const params = useParams();
    const dispatchPromise = useDispatchPromise();
    const dispatch = useDispatch();
    const { id } = params;
    const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [sortOption, setSortOption] = useState<string>('newest');
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const { pagination, setPagination, handleRowsChange, handlePageChange } = usePagination(1, 6);
    const forceNoRefresh = useRef<boolean>(false);

    // Get data from redux store
    const {
        audition,
        applications,
        loading,
        error,
        searching,
        pagination: auditionPagination,
    } = useSelector(managedAuditionWithSubmissionsSelector);

    const getData = useCallback(
        async (withClear?: boolean) => {
            const response: any = await dispatchPromise(
                manageAudition({
                    auditionId: id!,
                    page: pagination.page,
                    limit: pagination.rows,
                    status: filterStatus !== 'all' ? filterStatus : undefined,
                    ...(searchTerm && { search: searchTerm }),
                    isSearch: forceNoRefresh.current,
                    sort: sortOption,
                    ...(roleFilter && roleFilter !== 'all' && { roleId: roleFilter }),
                }),
            );

            const { data } = response;
            if (data && data.pagination) {
                const {
                    pagination: { currentPage, pageSize, totalCount },
                } = data;
                setPagination({
                    page: currentPage,
                    rows: pageSize,
                    totalCount: totalCount,
                });
            }
            forceNoRefresh.current = false;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pagination.page, pagination.rows, sortOption, roleFilter, filterStatus, id, searchTerm],
    );

    useEffect(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        if (applications?.length >= 0 && auditionPagination.total) {
            setPagination(prev => ({
                ...prev,
                totalCount: auditionPagination.total,
            }));
        }
    }, [applications, setPagination, auditionPagination.total]);

    // Handle search input change
    const handleSearchChange = (searchText: string) => {
        forceNoRefresh.current = true;
        setSearchTerm(searchText);
        // Reset to first page when searching
    };

    // Handle filter status change
    const handleFilterStatusChange = (status: string) => {
        setFilterStatus(status);
    };

    // Handle sort option change
    const handleSortOptionChange = (option: string) => {
        setSortOption(option);
    };

    // Handle role filter change
    const handleRoleFilterChange = (roleId: string) => {
        setRoleFilter(roleId);
    };

    // Handle edit roles click
    const handleEditRolesClick = () => {
        console.log('Edit here');
    };

    // Handle view details click
    const handleViewDetailsClick = (application: IApplication) => {
        console.log('View application details', application);
        // Additional functionality to load the application details
        // or prepare data for the WorkProfile component
    };

    // Handle application accept
    const handleAcceptApplication = async (application: IApplication) => {
        await dispatchPromise(
            sortApplication({
                id: application._id,
                sortType: 'yes',
            }),
        );
    };

    // Handle application reject
    const handleRejectApplication = async (application: IApplication) => {
        await dispatchPromise(
            sortApplication({
                id: application._id,
                sortType: 'no',
            }),
        );
    };

    // Handle sending message to applicant
    const handleMessageApplicant = (application: IApplication) => {
        console.log('Message to applicant', application);
        // Implement logic to open a messaging interface
    };

    // Handle archiving application
    const handleArchiveApplication = async (application: IApplication) => {
        await dispatchPromise(
            sortApplication({
                id: application._id,
                sortType: 'archived',
            }),
        );
    };

    // Handle casting applicant
    const handleCastApplicant = async (application: IApplication) => {
        await dispatchPromise(
            sortApplication({
                id: application._id,
                sortType: 'cast',
            }),
        );
        /** Handle other details here */
    };

    const handleUpdateField = useCallback(
        (input: UpdateField) => {
            if (!id) return;

            let body: Partial<ICreateAuditionPayload> = {};
            if (Array.isArray(input)) {
                input.forEach(({ key, value }) => {
                    body[key] = value;
                });
            } else {
                body[input.key] = input.value;
            }

            dispatch(
                updateAudition({
                    id,
                    body: body as ICreateAuditionPayload,
                    isImageUpload: false,
                }),
            );
        },
        [id, dispatch],
    );

    return (
        <MetaTags title={`Manage Audition | ${audition?.title || 'Loading...'}`} description={audition?.synopsis || ''}>
            <ManageAuditionContainer
                audition={audition}
                applications={applications || []}
                loading={loading}
                error={error}
                pagination={pagination}
                filterStatus={filterStatus}
                sortOption={sortOption}
                roleFilter={roleFilter}
                onSearchChange={handleSearchChange}
                onFilterStatusChange={handleFilterStatusChange}
                onSortOptionChange={handleSortOptionChange}
                onRoleFilterChange={handleRoleFilterChange}
                onRowsChange={handleRowsChange}
                onPageChange={handlePageChange}
                onEditRolesClick={handleEditRolesClick}
                onViewDetailsClick={handleViewDetailsClick}
                onAcceptApplication={handleAcceptApplication}
                onRejectApplication={handleRejectApplication}
                onMessageApplicant={handleMessageApplicant}
                onArchiveApplication={handleArchiveApplication}
                onCastApplicant={handleCastApplicant}
                onClearSearch={() => {
                    forceNoRefresh.current = true;
                    setSearchTerm(undefined);
                    getData(true);
                }}
                searchLoading={searching}
                onImageChange={(imageUrl, rawFile) => {
                    handleUpdateField([
                        { key: 'imageCover', value: [rawFile] },
                        { key: 'tempImageCover', value: imageUrl },
                    ]);
                }}
            />
        </MetaTags>
    );
};

export { ManageAudition };
